import { memo, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import moment from "moment";
import { isEqual, isNil } from "lodash";
import { Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import { bmColorGuide } from "../../../bedManagement/bmUtils";
import config from "../../../../app/config";

export const useStyles = makeStyles(() => ({
  root: {
    gap: 8,
    maxWidth: "fit-content",
    flexDirection: ({ direction }) => direction || "row",
  },
  typography: {
    minWidth: 85,
    fontWeight: 400,
    fontSize: ".8rem",

    "&.colorText": {
      color: ({ color }) => (!isNil(color) ? bmColorGuide[color] : null),
    },
    "&.timeoutText": {
      color: bmColorGuide.red,
    },
  },
}));

const BarsTimer = ({
  targetTime = moment().toISOString(),
  message,
  showDate,
  hideHour,
  dateFormat,
  boxDirection,
  rootClasses,
  calculateColor, // Ability to pass function returning timer font color;
}) => {
  const [timeLeft, setTimeLeft] = useState({});
  const [isTimeout, setIsTimeout] = useState(false);
  const [fontColor, setFontColor] = useState("green"); // 'green' | 'yellow' | 'red';

  const classes = useStyles({ color: fontColor, direction: boxDirection });

  const outputDateFormat = useMemo(() => {
    if (!showDate) return null;
    if (dateFormat) return dateFormat;

    if (showDate && hideHour) {
      return config?.defaultDateFormat;
    } else if (!showDate && !hideHour) {
      return config?.defaultTimeFormat;
    } else {
      return config?.defaultDateTimeFormat;
    }
  }, [showDate, hideHour, dateFormat]);

  const defaultCalculateColor = (diff) => {
    const hoursLeft = diff.asHours();

    if (hoursLeft >= 3) {
      return "green";
    } else if (hoursLeft < 3 && hoursLeft > 1) {
      return "yellow";
    } else {
      return "red";
    }
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = moment();
      const end = moment(targetTime);
      const diff = moment.duration(end.diff(now));

      if (diff.asSeconds() <= 0) {
        setIsTimeout(true);
        setTimeLeft({});
      } else {
        if (isTimeout) setIsTimeout(false);

        setTimeLeft({
          days: Math.floor(diff.asDays()),
          hours: Math.floor(diff.hours()),
          minutes: Math.floor(diff.minutes()),
          seconds: Math.floor(diff.seconds()),
        });

        const selectedColor = calculateColor
          ? calculateColor(diff)
          : defaultCalculateColor(diff);
        setFontColor(selectedColor);
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 10000);

    return () => clearInterval(interval);
  }, [targetTime, isTimeout, calculateColor]);

  const timeLeftString = useMemo(() => {
    if (isTimeout) return "-";

    let strArr = [];

    if (timeLeft.days > 0) strArr.push(`${timeLeft.days}D`);
    if (timeLeft.hours > 0) strArr.push(`${timeLeft.hours}H`);
    if (timeLeft.minutes > 0) strArr.push(`${timeLeft.minutes}M`);

    return strArr.join(" ");
  }, [timeLeft.days, timeLeft.hours, timeLeft.minutes, isTimeout]);

  return (
    <Stack className={clsx(classes.root, rootClasses && rootClasses)}>
      {isTimeout ? (
        <Typography
          variant="subtitle2"
          fontWeight={500}
          className={clsx(
            classes.typography,
            isTimeout ? "timeoutText" : "colorText",
          )}
        >
          {message}
        </Typography>
      ) : (
        <Typography
          variant="subtitle2"
          fontWeight={500}
          className={clsx(classes.typography, "colorText")}
        >
          {timeLeftString}
        </Typography>
      )}

      {showDate && (
        <Typography variant="subtitle2" className={classes.typography}>
          {moment(targetTime).format(outputDateFormat)}
        </Typography>
      )}
    </Stack>
  );
};

BarsTimer.propTypes = {
  targetTime: PropTypes.string.isRequired,
  message: PropTypes.string,
  showDate: PropTypes.bool,
  hideHour: PropTypes.bool,
  boxDirection: PropTypes.oneOf(["row", "column"]),
  rootClasses: PropTypes.string,
  calculateColor: PropTypes.func,
};

BarsTimer.defaultProps = {
  message: "TIMES UP",
  showDate: true,
  hideHour: true,
  boxDirection: "row",
};

// export default memo(BarsTimer, isEqual)
export default BarsTimer;
